import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AppHeader } from "@/components/observer/AppHeader";
import { ConfigDiff } from "@/components/compare/ConfigDiff";
import { TimelineColumn } from "@/components/compare/TimelineColumn";
import { api, errMsg } from "@/lib/api";
import { inputCls, labelCls } from "@/lib/ui";

function defaultPair(list) {
  const replays = list.filter((s) => s.mode === "replay");
  for (const s of replays) {
    const twin = replays.find((o) => o.id !== s.id && o.recording && o.recording === s.recording);
    if (twin) return [twin.id, s.id];
  }
  return [list[1]?.id, list[0]?.id];
}

const Picker = ({ label, value, onChange, sessions, testid }) => (
  <label className="space-y-2 block flex-1 min-w-[260px]">
    <span className={labelCls}>{label}</span>
    <select className={`${inputCls} font-mono text-xs`} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">Select a session…</option>
      {sessions.map((s) => (
        <option key={s.id} value={s.id}>{`${s.title} · ${s.mode}${s.recording ? ` · ${s.recording}` : ""} · ${s.id}`}</option>
      ))}
    </select>
  </label>
);

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [recs, setRecs] = useState({});
  const a = params.get("a");
  const b = params.get("b");

  useEffect(() => {
    api.get("/sessions").then(({ data }) => {
      setSessions(data);
      if (!a && !b && data.length > 1) {
        const [da, db] = defaultPair(data);
        setParams({ a: da, b: db }, { replace: true });
      }
    }).catch((e) => toast.error(errMsg(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    [a, b].filter(Boolean).forEach((id) => {
      if (recs[id]) return;
      api.get(`/sessions/${id}/record`).then(({ data }) => setRecs((r) => ({ ...r, [id]: data }))).catch((e) => toast.error(errMsg(e)));
    });
  }, [a, b, recs]);

  const set = (k) => (v) => setParams({ a, b, [k]: v });
  const A = recs[a];
  const B = recs[b];
  const sameRecording = A && B && A.replay?.filename && A.replay?.filename === B.replay?.filename;

  return (
    <div className="noise min-h-screen bg-[var(--o-bg)] text-[var(--o-fg)]" data-testid="compare-page">
      <AppHeader />
      <main className="relative z-10 max-w-7xl px-6 sm:px-10 pb-32">
        <div className="pt-12 pb-8 rise">
          <p className={labelCls}>Replay comparison</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-3">Before / after.</h1>
          <p className="text-sm sm:text-base text-[var(--o-muted)] mt-4 max-w-2xl">Replay the same recording after changing a prompt or threshold in Admin, then line the two runs up here.</p>
        </div>
        <div className="flex flex-wrap gap-4 mb-6">
          <Picker label="Run A (before)" value={a} onChange={set("a")} sessions={sessions} testid="compare-select-a" />
          <Picker label="Run B (after)" value={b} onChange={set("b")} sessions={sessions} testid="compare-select-b" />
        </div>
        {sessions.length < 2 && <p className="text-sm text-[var(--o-muted3)]" data-testid="compare-empty">You need at least two sessions to compare. Replay the same recording twice.</p>}
        {A && B && (
          <div className="space-y-8">
            {!sameRecording && <p className="font-mono text-xs text-[#F59E0B]" data-testid="compare-different-recordings">These runs used different recordings; differences may come from the audio, not the settings.</p>}
            <ConfigDiff A={A} B={B} />
            <div className="grid md:grid-cols-2 gap-6">
              <TimelineColumn record={A} label="A" />
              <TimelineColumn record={B} label="B" />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
